import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Props = { children: ReactNode };
type State = { hasError: boolean };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (process.env.NODE_ENV !== 'production') {
      const log =
        typeof console !== 'undefined' && typeof console.error === 'function'
          ? console.error.bind(console)
          : () => {};
      log('ErrorBoundary', error, info.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 py-16 text-center">
          <h1 className="mb-2 text-xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mb-6 max-w-md text-gray-600">
            This page hit an unexpected error. Try reloading, or go back to the home page.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-[#0a66c2] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#004182]"
            >
              Reload page
            </button>
            <Link
              to="/"
              className="rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-[#0a66c2] transition-colors hover:bg-gray-50"
            >
              Back to home
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
